import { FC, useState } from 'react';
import { sumBy } from 'lodash';
import { Box } from '@mui/material';
import Typography from '@mui/material/Typography';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { styled } from '@mui/material/styles';
import { grey } from '@mui/material/colors';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Row } from 'further-ui/components/Layout';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { numberToCurrencyString } from 'further-ui/utils';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import { Transaction } from './types';
import getColumns from './getColumns';
import Subtransactions from './Subtransactions';
import AccountingTable from '../components/AccountingTable';

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  padding: 0,
  margin: '0 !important',
  borderTop: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: 'unset !important',
  overflow: 'visible',
  '&::before, &.Mui-expanded::before': {
    display: 'none !important',
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
  margin: 0,
  minHeight: '56px !important',
  '& .MuiAccordionSummary-content': {
    margin: 0,
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    marginLeft: theme.spacing(theme.spacingSizes.sm),
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.shorter,
    }),
    '& .MuiSvgIcon-root': {
      color: theme.palette.grey[600],
    },
  },
  '&.Mui-expanded .MuiAccordionSummary-content': {
    margin: 0,
  },
}));

const StyledAccordionDetails = styled(AccordionDetails)({
  padding: 0,
});

const AccountHoverScope = styled(Box)({
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    opacity: 0,
  },
  '&:hover .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    opacity: 1,
  },
});

const TotalText = styled(Typography)({
  fontSize: 'inherit',
});

const TotalRow = styled(Row)(({ theme }) => ({
  paddingTop: theme.spacing(theme.spacingSizes.xxs),
  paddingBottom: theme.spacing(theme.spacingSizes.xxs),
}));

const getExpandedContent =
  (baseCurrency: CurrencyCode) =>
  ({ subtransactions }: Transaction) =>
    (
      <Subtransactions
        subtransactions={subtransactions ?? []}
        baseCurrency={baseCurrency}
      />
    );

type Props = {
  accountLabel: string;
  transactions: Array<Transaction>;
  baseCurrency: CurrencyCode;
};

const Account: FC<Props> = ({ accountLabel, transactions, baseCurrency }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const { expandedRows, toggleRowExpansion } = useExpandedRows();

  const hasNoTransactions = transactions.length === 0;
  const columns = getColumns({ baseCurrency });
  const totalAmount = sumBy(
    transactions,
    (transaction) => transaction.amountIncVatLcy ?? 0,
  );
  const totalAmountFormatted = numberToCurrencyString(totalAmount, {
    currency: baseCurrency,
    negativeInBrackets: true,
  });

  return (
    <AccountHoverScope>
      <StyledAccordion expanded={isExpanded}>
        <StyledAccordionSummary
          expandIcon={<ExpandMoreIcon fontSize="small" color="primary" />}
          aria-controls={`general-ledger-account-${accountLabel}`}
          id={`general-ledger-account-${accountLabel}`}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Row fullWidth centered justify="space-between">
            <Typography variant="h6">{accountLabel}</Typography>
            {!isExpanded ? (
              <Typography variant="h6">{totalAmountFormatted}</Typography>
            ) : null}
          </Row>
        </StyledAccordionSummary>
        <StyledAccordionDetails>
          <AccountingTable
            columns={columns}
            rows={transactions}
            emptyMessage="You have no transactions."
            bodyRowPaddingY={hasNoTransactions ? 30 : undefined}
            noPaddingX={hasNoTransactions}
            isExpandableRow={({ subtransactions }) =>
              !!subtransactions && subtransactions.length > 0
            }
            isExpandedRow={({ id }) => expandedRows.has(id)}
            onRowExpand={({ id }) => toggleRowExpansion(id)}
            onRowClick={({ id }) => toggleRowExpansion(id)}
            getExpandedContent={getExpandedContent(baseCurrency)}
            stickyExpandedContent={false}
            borderless
            customFooterRow={[
              {
                key: 'total',
                content: (
                  <TotalRow centered spacing="xs">
                    <TotalText variant="h6">Subtotal</TotalText>
                    <TotalText variant="body2">{accountLabel}</TotalText>
                  </TotalRow>
                ),
                colspan: 5,
                backgroundColor: grey[50],
              },
              {
                key: 'totalAmount',
                content: totalAmountFormatted,
                colspan: 6,
                backgroundColor: grey[50],
              },
            ]}
          />
        </StyledAccordionDetails>
      </StyledAccordion>
    </AccountHoverScope>
  );
};

export default Account;
